import { devlog } from "./utils.ts";

/**
 * Typed element query. Two ways in:
 *  - the component's class:      wcQry(AsImportGroup)          -> AsImportGroup | null
 *  - its tag name, if registered on HTMLElementTagNameMap — the DOM lib's own
 *    tag -> type registry (the thing that already makes
 *    `document.querySelector('div')` return HTMLDivElement). A component
 *    opts in with a declaration-merge in its own file:
 *
 *      declare global {
 *        interface HTMLElementTagNameMap {
 *          'as-import-group': AsImportGroup;
 *        }
 *      }
 *
 *    Once registered, wcQry('as-import-group') AND plain
 *    document.querySelector('as-import-group') both resolve to
 *    AsImportGroup | null — no bespoke registry needed, reusing the one
 *    TypeScript already ships. Not a retrofit of every component; add an
 *    entry when you want the string form typed for that one.
 *  - any other tag-name string (not registered, or a non-TS caller with no
 *    class import to hand over — demo `<script>` blocks, plain-JS consumers
 *    of the built bundle): wcQry('as-foo') -> HTMLElement | null, untyped.
 *
 * Either way you also get whatever autoAttrs() (util/autoAttrs.ts) added to
 * the component, since those are just regular instance properties by the
 * time it reaches here:
 *
 *   const group = wcQry(AsImportGroup);
 *   group?.subheadingDefaultText;      // string | null — typed, from autoAttrs
 *   group?.fileInput.openFilePicker(); // regular element APIs work too
 *
 * The tag name for the class form is read off the constructor's own static
 * `tag` (the same string every component already passes to
 * customElements.define(), via AsEl.define() or by hand — see base.ts) —
 * no string literal to keep in sync with the component's actual registration.
 * Falls back to the native customElements.getName() (optional-chained, so it
 * degrades to undefined rather than throwing where the method doesn't exist —
 * older embedded Chromium/CEF webviews, observed: GrabCAD Print's, predate it)
 * only for a class with no static `tag`, e.g. one registered dynamically
 * under a runtime-computed name. Pass a selectorSuffix to narrow further
 * (id, class, attribute selector):
 *
 *   wcQry(AsModalOverlay, '#tutorial-overlay')  // as-modal-overlay#tutorial-overlay
 *
 * Returns null both when nothing matches and when a passed class was never
 * registered with customElements.define() (logged via devlog in the latter
 * case — that's a bug at the call site, not a normal "not found yet").
 */
function tagFor(ctorOrTag: CustomElementConstructor | string): string | null {
  if (typeof ctorOrTag === 'string') return ctorOrTag;
  return (ctorOrTag as CustomElementConstructor & { tag?: string }).tag || customElements.getName?.(ctorOrTag) || null;
}

export function wcQry<T extends CustomElementConstructor>(ctor: T, selectorSuffix?: string, root?: ParentNode): InstanceType<T> | null;
export function wcQry<K extends keyof HTMLElementTagNameMap>(tag: K, selectorSuffix?: string, root?: ParentNode): HTMLElementTagNameMap[K] | null;
export function wcQry(tag: string, selectorSuffix?: string, root?: ParentNode): HTMLElement | null;
export function wcQry(
  ctorOrTag: CustomElementConstructor | string,
  selectorSuffix = '',
  root: ParentNode = document,
): HTMLElement | null {
  const tag = tagFor(ctorOrTag);
  if (!tag) {
    devlog(`[wcQry] ${(ctorOrTag as CustomElementConstructor).name} is not a registered custom element — did you forget customElements.define()?`);
    return null;
  }
  return root.querySelector<HTMLElement>(`${tag}${selectorSuffix}`);
}

/**
 * Plural form of wcQry() — same class-or-tag-name lookup (including the
 * HTMLElementTagNameMap-typed string form, see wcQry() above), querySelectorAll
 * instead of querySelector. Returns a plain array (not a NodeList) so
 * map/filter/etc. work directly:
 *
 *   wcQryAll(AsHovercard).forEach(card => card.close());
 *   wcQryAll('as-hovercard');  // untyped HTMLElement[] form unless registered
 *
 * Returns [] both for no matches and for an unregistered class (logged via
 * devlog in the latter case, same as wcQry()).
 */
export function wcQryAll<T extends CustomElementConstructor>(ctor: T, selectorSuffix?: string, root?: ParentNode): InstanceType<T>[];
export function wcQryAll<K extends keyof HTMLElementTagNameMap>(tag: K, selectorSuffix?: string, root?: ParentNode): HTMLElementTagNameMap[K][];
export function wcQryAll(tag: string, selectorSuffix?: string, root?: ParentNode): HTMLElement[];
export function wcQryAll(
  ctorOrTag: CustomElementConstructor | string,
  selectorSuffix = '',
  root: ParentNode = document,
): HTMLElement[] {
  const tag = tagFor(ctorOrTag);
  if (!tag) {
    devlog(`[wcQryAll] ${(ctorOrTag as CustomElementConstructor).name} is not a registered custom element — did you forget customElements.define()?`);
    return [];
  }
  return Array.from(root.querySelectorAll<HTMLElement>(`${tag}${selectorSuffix}`));
}

// AsEl (base class for new components — static tag, define(), scoped wcQry(),
// .typed) lives in components/base.ts, not here: it needs to sit alongside
// the other component base classes (as-reactive-component.ts etc.), and it
// depends on wcQry() above rather than the other way around.

/** Attribute values `createElWithAttrs` accepts. `false`/`null`/`undefined`
 *  skip the attribute entirely, so optional ones need no `if` at the call site. */
export type AttrValue = string | number | boolean | null | undefined;

/** Children for `createElWithAttrs`. A string or number becomes a text node;
 *  falsy entries in an array are dropped, so `cond && el` works inline. */
export type ElContent = string | number | Node | Array<string | number | Node | null | undefined | false>;

/**
 * Create an element and set its attributes in one call — the imperative
 * counterpart to building a markup string. Takes the same two ways in as
 * wcQry(): the component's class, or its tag name.
 *
 *   createElWithAttrs(AsCheckbox, { 'label-dict-key': 'upload.sync' })
 *   createElWithAttrs('as-option', { value: '1' }, 'Option one')
 *   createElWithAttrs('as-dropdown', { 'label-default-text': label }, options)
 *
 * The class form is typed through `InstanceType<T>`; the tag-name form is typed
 * when that tag has an HTMLElementTagNameMap entry, and `HTMLElement` otherwise
 * (see wcQry above for the declaration-merge that opts a component in).
 *
 * Attribute values: strings and numbers are set as-is (numbers stringified),
 * `true` sets the empty-string attribute a boolean attribute wants, and
 * `false`/`null`/`undefined` skip it. That last one is the point — an optional
 * attribute is `{ step: style.step }`, not a follow-up `if`.
 *
 * `children` is a third argument rather than a reserved key in the attrs
 * object, so a component that really has a `text` or `children` attribute
 * still works. A string or number becomes text content; an array may hold
 * nodes, strings and falsy values, and the falsy ones are dropped.
 *
 * `passAttributes()` further down is the string-building counterpart, for when
 * you are interpolating into a template rather than creating an element.
 */
export function createElWithAttrs<T extends CustomElementConstructor>(ctor: T, attrs?: Record<string, AttrValue>, children?: ElContent): InstanceType<T>;
export function createElWithAttrs<K extends keyof HTMLElementTagNameMap>(tag: K, attrs?: Record<string, AttrValue>, children?: ElContent): HTMLElementTagNameMap[K];
export function createElWithAttrs(tag: string, attrs?: Record<string, AttrValue>, children?: ElContent): HTMLElement;
export function createElWithAttrs(
  ctorOrTag: CustomElementConstructor | string,
  attrs: Record<string, AttrValue> = {},
  children?: ElContent
): HTMLElement {
  const tag = tagFor(ctorOrTag);
  // Only reachable from the class form: a constructor with no static `tag` that
  // customElements never saw. There is no element we could sensibly return, and
  // handing back null would put a null check on every call site.
  if (!tag) throw new Error('[createElWithAttrs] no tag name for the given constructor — it has no static `tag` and was never passed to customElements.define()');

  const el = document.createElement(tag);

  for (const [name, value] of Object.entries(attrs)) {
    if (value === false || value === null || value === undefined) continue;
    if (value === true) { el.setAttribute(name, ''); continue; }
    if (typeof value === 'string' || typeof value === 'number') { el.setAttribute(name, String(value)); continue; }
    devlog(`[createElWithAttrs] <${tag} ${name}> skipped: attributes take a string, number or boolean, got ${typeof value}`);
  }

  if (children !== undefined) appendContent(el, children);
  return el;
}

/** Shared by createElWithAttrs and replaceChildren below. */
function appendContent(el: HTMLElement, children: ElContent): void {
  const list = Array.isArray(children) ? children : [children];
  for (const child of list) {
    if (child === null || child === undefined || child === false) continue;
    el.append(typeof child === 'string' || typeof child === 'number' ? String(child) : child);
  }
}

/**
 * Swap a container's contents without the `innerHTML = ...` rebuild.
 *
 * Re-running `innerHTML` on a container holding custom elements destroys and
 * recreates every one of them, so they lose their state, their listeners and
 * their disconnectedCallback cleanup — the reason `<as-lang-selector>` used to
 * come back blank whenever `as-header` re-rendered. This replaces the children
 * as nodes, which leaves anything you kept a reference to alive.
 *
 * Building a container's markup *once* with innerHTML is fine and the library
 * still does it. This is for the second and later times.
 */
export function replaceChildren(el: HTMLElement | null | undefined, children: ElContent): void {
  if (!el) return;
  el.replaceChildren();
  appendContent(el, children);
}


/**
 * Force-hides (or reveals) any element via an inline `display` override
 * (`!important`) — the technique needed to hide a component out from under
 * its own `:host { display: ... }` rule, since an inline style only beats
 * that short of the rule being `!important` too. Un-hiding *removes* the
 * inline property rather than setting a value, so the element falls back to
 * whatever its stylesheet says.
 *
 * This is the free-function half of `AsEl.hide()`, which delegates here — the
 * same pairing as `this.wcQry()` -> `wcQry()` above. Use this one for plain
 * elements and static markup that aren't AsEl instances.
 */
export function setHidden(el: HTMLElement | null | undefined, hidden: boolean): void {
  if (!el) return;
  if (hidden) el.style.setProperty('display', 'none', 'important');
  else el.style.removeProperty('display');
}

/** Enables or disables the `<as-step-panel>` with the given step id. */
export function setStepEnabled(step: string, isEnabled: boolean): void {
  const stepEl = document.querySelector(`as-step-panel#${step}`);
  if (isEnabled) stepEl?.removeAttribute('disabled');
  else stepEl?.setAttribute('disabled', 'true');
}

/**
 * Checks if an element has an attribute that is considered "true"
 * An attribute is considered true if:
 * 1. It exists AND
 * 2. Its value is not explicitly "false"
 * 
 * @param element The element to check
 * @param attributeName The name of the attribute to check
 * @returns boolean True if the attribute exists and is not "false"
 */
export const hasEnabledAttribute = (
  element: HTMLElement,
  attributeName: string
): boolean => {
  if (!element.hasAttribute(attributeName)) {
    return false;
  }
  
  const value = element.getAttribute(attributeName)?.toLowerCase();
  return value !== "false";
};

/**
 * Generates an HTML attribute string from matching attributes on the source element
 * @param element The source element to get attributes from
 * @param attributeList Array of attribute names to pass through
 * @param excludeEmpty Whether to exclude attributes with empty values
 * @returns String of HTML attributes in "name='value'" format
 */
export const passAttributes = (
  element: HTMLElement, 
  attributeList: string[], 
  excludeEmpty = false
): string => {
  return attributeList
    .filter(attr => element.hasAttribute(attr))
    .map(attr => {
      const value = element.getAttribute(attr);
      
      // For boolean attributes (those without values)
      if (value === '' || value === null) {
        return excludeEmpty ? '' : attr;
      }
      
      // For attributes with values
      return `${attr}="${value}"`;
    })
    .filter(attr => attr !== '')
    .join(' ');
};

// Define interface for component options
interface ComponentOptions {
  dynamicStyles?: (component: HTMLElement) => string;
  addEventListeners?: () => void;
}

// Helper to create web components with optional dynamic styles
export const defComponent = (tagname: string, template: string | ((el: HTMLElement) => string), styles: string, opts: ComponentOptions = {}) => {
  customElements.define(tagname, class extends HTMLElement {
    private _addEventListeners?: () => void = opts.addEventListeners;
    
    constructor() {
      super()
      this.attachShadow({ mode: 'open' })
      this.render()
    }
    
    // Observe these attributes for changes
    static get observedAttributes() {
      return ['left-text', 'center-text', 'right-text']
    }
    
    // Re-render when attributes change
    attributeChangedCallback() {
      this.render()
    }
    
    render() {
      const dynamicStyles = opts.dynamicStyles?.(this) ?? ''
      
      this.shadowRoot.innerHTML = /*html*/`
        <style>
          ${styles}
          ${dynamicStyles}
        </style>
        ${typeof template === 'function' ? template(this) : template}
      `
    }
    
    connectedCallback() {
      this._addEventListeners?.()
    }
  })
}

// Global registry for HMR: maps CSS file paths to elements that use them
if (typeof window !== 'undefined') {
  (window as any).__styleRegistry = (window as any).__styleRegistry || new Map();
}

export function useShadow(host:HTMLElement&{html?:string},initHTML?:string){
  if (!host.shadowRoot) {
    host.attachShadow({ mode: 'open' });
  }
  host.shadowRoot.innerHTML = initHTML??host?.html??`No HTML Provided to useShadow!`
}

export async function useShadowStyle(
  host: HTMLElement & { styleSheet?: CSSStyleSheet; __cssPath?: string },
  styleText: string,
  cssPath: string
) {
  devlog('[useShadowStyle] Called with styleText:', styleText?.substring(0, 50));
  host.styleSheet = new CSSStyleSheet();
  await host.styleSheet.replace(styleText);
  host.shadowRoot.adoptedStyleSheets = [...host.shadowRoot.adoptedStyleSheets, host.styleSheet];
  devlog('[useShadowStyle] Styles applied to', host.tagName);

  // Register element for HMR
  if (typeof window !== 'undefined') {
    host.__cssPath = cssPath;
    const registry = (window as any).__styleRegistry;
    if (!registry.has(cssPath)) {
      registry.set(cssPath, new Set());
    }
    registry.get(cssPath).add(host);
    devlog('[useShadowStyle] Registered element for HMR:', host.tagName, cssPath);
  }
}
